import React from 'react';

import { Button, message, Popconfirm, Space } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { IError } from '@app/models/error';
import { Module, ParticipationRowStatus, RetrieveParticipationSchema } from '@app/models/users';
import Api from '@app/services/api';
import { getUnfinishedOrdersError } from '@app/services/apiErrors';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';

import { SwitchedOffCatalogsModal } from '@pages/Warehouses/components/SwitchedOffCatalogsModal';
import { UnfinishedOrdersModal } from '@pages/Warehouses/components/UnfinishedOrdersModal';

import {
  useDeactivateParticipationMutation,
  useReactivateParticipationMutation,
  useRevokeInvitationMutation,
} from '../../../../hooks';

interface IProps {
  participation: RetrieveParticipationSchema;
  disabled: boolean;
  onInvite: (participation: RetrieveParticipationSchema) => void;
  onPoas?: (participation: RetrieveParticipationSchema) => void;
}

export const ParticipationActions: React.FC<IProps> = ({ participation, disabled, onInvite, onPoas }) => {
  const spaceT = useTranslation(NSKeys.spaceSettings);
  const commonT = useTranslation(NSKeys.common);
  const getErrorMessage = useApiErrorMessage();
  const revoke = useRevokeInvitationMutation();
  const deactivate = useDeactivateParticipationMutation();
  const reactivate = useReactivateParticipationMutation();
  const [unfinishedOrders, setUnfinishedOrders] = React.useState<IError>();
  const [switchedOff, setSwitchedOff] = React.useState<string[]>();

  const { status, $permissions } = participation;
  const isAwaiting = status === ParticipationRowStatus.AwaitingActivation;
  const canInvite = !!$permissions.invite && (status === ParticipationRowStatus.Inactive || isAwaiting);
  const canRevoke = !!$permissions.invite && isAwaiting;
  const canDeactivate = !!$permissions.deactivate && status !== ParticipationRowStatus.Deactivated;
  const canReactivate = !!$permissions.reactivate && status === ParticipationRowStatus.Deactivated;

  const showSwitchedOffCatalogs = () =>
    Api.wms
      .getWarehouses({ limit: 1000 })
      .then(({ items }) =>
        setSwitchedOff(
          items
            .filter((warehouse) => warehouse.legal_entity_uid === participation.legal_entity.uid)
            .map((warehouse) => warehouse.uid)
        )
      )
      .catch((err) => message.error(getErrorMessage(err)));

  const callbacks = (successText: string, afterSuccess?: () => void) => ({
    onSuccess: () => {
      message.success(successText);
      afterSuccess?.();
    },
    onError: (err: unknown) => {
      const refusal = getUnfinishedOrdersError(err);
      if (refusal) {
        setUnfinishedOrders(refusal);
      } else {
        message.error(getErrorMessage(err));
      }
    },
  });

  return (
    <>
      <Space size={0} wrap>
        {onPoas && participation.module === Module.Delivery && (
          <Button type="link" size="small" disabled={disabled} onClick={() => onPoas(participation)}>
            {spaceT.t('coalition.actions.poas')}
          </Button>
        )}
        {canInvite && (
          <Button type="link" size="small" disabled={disabled} onClick={() => onInvite(participation)}>
            {isAwaiting ? spaceT.t('coalition.actions.inviteAgain') : commonT.t('buttonInvite')}
          </Button>
        )}
        {canRevoke && (
          <Popconfirm
            title={spaceT.t('coalition.actions.revokeConfirm')}
            okText={commonT.t('confirm')}
            cancelText={commonT.t('cancel')}
            disabled={disabled}
            onConfirm={() => revoke.mutate(participation.uid, callbacks(spaceT.t('coalition.actions.revokeSuccess')))}
          >
            <Button type="link" size="small" disabled={disabled} loading={revoke.isLoading}>
              {spaceT.t('coalition.actions.revoke')}
            </Button>
          </Popconfirm>
        )}
        {canDeactivate && (
          <Popconfirm
            title={spaceT.t('coalition.actions.deactivateConfirm')}
            description={spaceT.t('coalition.actions.deactivateDescription')}
            okText={commonT.t('confirm')}
            okButtonProps={{ danger: true }}
            cancelText={commonT.t('cancel')}
            disabled={disabled}
            onConfirm={() =>
              deactivate.mutate(
                participation.uid,
                callbacks(
                  spaceT.t('coalition.actions.deactivateSuccess'),
                  participation.module === Module.Warehouse ? showSwitchedOffCatalogs : undefined
                )
              )
            }
          >
            <Button type="link" size="small" danger disabled={disabled} loading={deactivate.isLoading}>
              {spaceT.t('coalition.actions.deactivate')}
            </Button>
          </Popconfirm>
        )}
        {canReactivate && (
          <Button
            type="link"
            size="small"
            disabled={disabled}
            loading={reactivate.isLoading}
            onClick={() =>
              reactivate.mutate(participation.uid, callbacks(spaceT.t('coalition.actions.reactivateSuccess')))
            }
          >
            {spaceT.t('coalition.actions.reactivate')}
          </Button>
        )}
      </Space>
      <UnfinishedOrdersModal error={unfinishedOrders} onClose={() => setUnfinishedOrders(undefined)} />
      <SwitchedOffCatalogsModal warehouseUids={switchedOff} onClose={() => setSwitchedOff(undefined)} />
    </>
  );
};
